/**
 * Serializes a Dolphin analytics event into the ERS shape the Dolphin_Event ->
 * Firehose -> S3 -> Redshift pipeline already consumes. Pure: all environment
 * arrives via `context`, so it is unit-testable without mocks.
 *
 * The envelope must satisfy both JSONPaths files off the stream (3-month reads the
 * platform pair nested, 15-day flat). Invariants:
 *   1. Emit the OBSERVED PWA shape, NOT the native shape: application.sdk and
 *      device.platform are EMPTY OBJECTS and package/version/model/make/locale stay
 *      unpopulated. Populating them would newly fill Redshift columns that are NULL
 *      for PWA rows today (a silent data change; consumers may key off those NULLs to
 *      separate PWA from native rows).
 *   2. attributes are ITERATED, not enumerated, so extra attributes pass through
 *      without branch-specific code.
 *   3. session.start_timestamp is epoch MILLISECONDS as a number (COPY TIMEFORMAT
 *      'epochmillisecs'). Amplify sent an ISO-8601 string and ERS converted it; this
 *      producer writes the record directly, so it emits the number itself.
 */
const EVENT_VERSION = "3.1";

export function serializeAnalyticsEvent(dolphinEvent, context, timestampMs) {
    // session is nested; guard against a partially built context.
    const session = context.session || {};

    const attributes = {};
    // dolphinEvent.attributes is a dolphinAttributesBuilder INSTANCE, not a plain
    // object. Safe to iterate: setters assign own enumerable props matching the field
    // names, methods live on the prototype so Object.keys skips them. Same for metrics.
    const source = (dolphinEvent && dolphinEvent.attributes) || {};
    Object.keys(source).forEach((key) => {
        const value = source[key];
        // Skip null/undefined/empty, matching production (absent optional attributes).
        if (value !== null && value !== undefined && value !== "") {
            attributes[key] = String(value);
        }
    });

    const metrics = {};
    const metricSource = (dolphinEvent && dolphinEvent.metrics) || {};
    Object.keys(metricSource).forEach((key) => {
        const value = metricSource[key];
        if (typeof value === "number" && isFinite(value)) {
            metrics[key] = value;
        }
    });

    return JSON.stringify({
        event_type: dolphinEvent.name,
        event_timestamp: timestampMs,
        // Equals event_timestamp: a direct producer has no server hop. The old delta
        // was clock skew, not latency; use Firehose metrics for ingestion latency.
        arrival_timestamp: timestampMs,
        event_version: EVENT_VERSION,
        application: {
            app_id: context.appId,
            cognito_identity_pool_id: context.cognitoIdentityPoolId,
            // EMPTY OBJECT, matching PWA production. No flat sdk_name/sdk_version either.
            sdk: {}
        },
        client: {
            client_id: context.clientId,
            // "" until the first credential resolution completes, as native does.
            cognito_id: context.cognitoId || ""
        },
        device: {
            // EMPTY OBJECT, matching PWA production. model/make/locale reach Redshift
            // via attributes.device_modal/device_manufacture/locale instead.
            platform: {}
        },
        session: {
            // One session per page load, minted lazily and never rotated, mirroring
            // Amplify's _generateSession. Not from SessionManager, which rotates on
            // every WebSocket reconnect and would fragment PARTITION BY session_id.
            session_id: session.id,
            start_timestamp: session.startTimestamp
        },
        attributes: attributes,
        metrics: metrics
    });
}
